/* shared/data-table — table/list interaction primitives.
   Extracted from services.jsx: exportCSV, useTableState, useFilters,
   FilterChip, BulkBar, RowActions, SortableTh. ESM. */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NexusIcon } from '../ui/NexusIcon';
import { Btn, IconBtn } from '../ui';
import { useStore } from '../state';
import { useRbac } from '../../rbac/RbacContext';

/* ---------- exportCSV ---------- */
export const exportCSV = (filename, rows) => {
  if (!rows.length) return;
  const cols = Object.keys(rows[0]).filter(k => typeof rows[0][k] !== 'object');
  const esc = (v) => {
    const s = (v == null ? '' : String(v)).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const csv = [cols.join(','), ...rows.map(r => cols.map(c => esc(r[c])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename + '.csv'; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/* ---------- useTableState ---------- */
export const useTableState = (module, allRows, defaultSort = null) => {
  const [selected, setSelected] = useState(new Set());
  const [sort, setSort] = useState(() => {
    try {
      const raw = sessionStorage.getItem('nx-sort-' + module);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.field === 'string' && parsed.field) return parsed;
    } catch (_) {}
    return null;
  });

  useEffect(() => {
    setSelected(prev => {
      const valid = new Set(allRows.map(r => r.id));
      const next = new Set([...prev].filter(id => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [allRows]);

  const toggleOne = useCallback((id) => {
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);
  const toggleAll = useCallback((rows) => {
    setSelected(s => {
      const allSelected = rows.length > 0 && rows.every(r => s.has(r.id));
      return allSelected ? new Set() : new Set(rows.map(r => r.id));
    });
  }, []);
  const clear = useCallback(() => setSelected(new Set()), []);

  const onSort = useCallback((field) => {
    setSort(s => !s || s.field !== field ? { field, dir: 'asc' } : s.dir === 'asc' ? { field, dir: 'desc' } : null);
  }, []);

  /* Stable primitives avoid object-identity churn in applySort's dep array */
  const dfField = defaultSort?.field ?? null;
  const dfDir   = defaultSort?.dir   ?? null;

  const applySort = useCallback((rows) => {
    /* Explicit user sort takes precedence; defaultSort applies only when sort is null */
    const effectiveSort = sort || (dfField ? { field: dfField, dir: dfDir } : null);
    if (!effectiveSort) return rows;
    const { field, dir } = effectiveSort;
    return [...rows].sort((a, b) => {
      const av = a[field], bv = b[field];
      if (av == null && bv == null) return String(b.id || '').localeCompare(String(a.id || ''));
      if (av == null) return 1;
      if (bv == null) return -1;
      let cmp;
      if (typeof av === 'number' && typeof bv === 'number') cmp = dir === 'asc' ? av - bv : bv - av;
      else cmp = dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      /* id DESC tie-breaker for identical primary values — deterministic stable order */
      return cmp !== 0 ? cmp : String(b.id || '').localeCompare(String(a.id || ''));
    });
  }, [sort, dfField, dfDir]);

  return { selected, setSelected, toggleOne, toggleAll, clear, sort, setSort, onSort, applySort };
};

/* ---------- useFilters ---------- */
export const useFilters = (module, defaultFilters = {}) => {
  const key = 'nx-filters-' + module;
  const [filters, setFiltersRaw] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(key)) || defaultFilters; } catch (_) { return defaultFilters; }
  });
  const [search, setSearchRaw] = useState(() => sessionStorage.getItem(key + '-search') || '');

  const setFilters = useCallback((next) => {
    setFiltersRaw(prev => {
      const updated = typeof next === 'function' ? next(prev) : next;
      try { sessionStorage.setItem(key, JSON.stringify(updated)); } catch (_) {}
      return updated;
    });
  }, [key]);

  const setFilter = useCallback((field, value) => {
    setFilters(f => {
      const n = { ...f };
      if (value == null || value === '' || value === 'any') delete n[field];
      else n[field] = value;
      return n;
    });
  }, [setFilters]);

  const setSearch = useCallback((v) => {
    setSearchRaw(v);
    try { sessionStorage.setItem(key + '-search', v); } catch (_) {}
  }, [key]);

  const clearAll = useCallback(() => { setFilters({}); setSearch(''); }, [setFilters, setSearch]);

  const activeCount = Object.keys(filters).length + (search ? 1 : 0);
  return { filters, setFilters, setFilter, search, setSearch, clearAll, activeCount };
};

/* ---------- FilterChip ---------- */
export const FilterChip = ({ icon, label, value, options, onChange, active, multi }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const sel = multi ? (Array.isArray(value) ? value : (value ? [value] : [])) : null;
  const isSelected = (v) => multi ? sel.includes(v) : value === v;
  const noneSelected = multi ? sel.length === 0 : !value;
  const displayVal = multi
    ? (sel.length === 0 ? 'All' : sel.length === 1 ? sel[0] : sel.length + ' selected')
    : (value || 'All');

  const handleItem = (v) => {
    if (multi) {
      const next = sel.includes(v) ? sel.filter(x => x !== v) : [...sel, v];
      onChange(next.length ? next : null);
    } else {
      onChange(v);
      setOpen(false);
    }
  };

  const handleAny = () => { onChange(null); setOpen(false); };

  return (
    <span ref={ref} className="nx-chip-wrap" style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        className={'nx-chip' + (active ? ' is-active' : '')}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {icon && <NexusIcon name={icon} size={11} />}
        {label}: <strong>{displayVal}</strong>
        <NexusIcon name="ChevronDown" size={10} />
      </button>
      {open && (
        <div className="nx-chip-menu" role="listbox" aria-label={label} aria-multiselectable={multi || undefined}>
          <button type="button" role="option" aria-selected={noneSelected} className={'nx-chip-menu-item' + (noneSelected ? ' is-active' : '')} onClick={handleAny}>
            <NexusIcon name="Check" size={11} style={{ opacity: noneSelected ? 1 : 0 }} />
            <span>All</span>
          </button>
          {options.map(o => {
            const v = typeof o === 'object' ? o.value : o;
            const l = typeof o === 'object' ? o.label : o;
            const selected = isSelected(v);
            return (
              <button type="button" role="option" aria-selected={selected} key={v} className={'nx-chip-menu-item' + (selected ? ' is-active' : '')} onClick={() => handleItem(v)}>
                <NexusIcon name="Check" size={11} style={{ opacity: selected ? 1 : 0 }} />
                <span>{l}</span>
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
};

/* ---------- BulkBar ---------- */
export const BulkBar = ({ count, type, ids, onClear, onExport, extraActions }) => {
  const store = useStore();
  const handleDelete = () => {
    store.confirm({
      title: 'Delete ' + count + ' ' + (count === 1 ? 'record' : 'records') + '?',
      description: 'This action cannot be undone. Linked deals and conversations will be removed.',
      danger: true,
      confirmLabel: 'Delete',
      onConfirm: () => { store.bulkRemove(type, ids); onClear(); },
    });
  };
  return (
    <div className="nx-bulk-bar">
      <span><strong>{count}</strong> selected</span>
      {extraActions}
      <Btn icon="File" variant="ghost" onClick={onExport}>Export</Btn>
      <span style={{ flex: 1 }} />
      <Btn icon="Trash" variant="danger-ghost" onClick={handleDelete}>Delete</Btn>
      <IconBtn icon="X" onClick={onClear} title="Clear selection" />
    </div>
  );
};

/* ---------- RowActions ---------- */
export const RowActions = ({ type, record }) => {
  const store = useStore();
  const { can } = useRbac();
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const canManage = can('crm.manage');

  return (
    <span ref={ref} className="nx-row-actions" onClick={e => e.stopPropagation()}>
      {/* Filled-circle icon renders ~2.7px dots at size=16 on non-retina — properly visible without hover */}
      <button type="button" className="nx-iconbtn nx-row-action-btn" onClick={() => setOpen(o => !o)} aria-label={`More actions for ${record.name || record.title || 'this record'}`} aria-haspopup="menu" aria-expanded={open}>
        <NexusIcon name="MoreHorizontalFilled" size={16} fill="currentColor" strokeWidth={0} />
      </button>
      {open && (
        <div className="nx-chip-menu" style={{ right: 0, left: 'auto' }} role="menu">
          <button type="button" role="menuitem" className="nx-chip-menu-item" onClick={() => { store.openDrawer(record); setOpen(false); }}>
            <NexusIcon name="ArrowUpRight" size={11} /><span>Open</span>
          </button>
          <button role="menuitem" className="nx-chip-menu-item" onClick={() => { store.openEditForm(type, record); setOpen(false); }}>
            <NexusIcon name="Edit" size={11} /><span>Edit</span>
          </button>
          {type === 'deals' && canManage && !record.archived && (
            <button role="menuitem" className="nx-chip-menu-item" onClick={() => {
              setOpen(false);
              store.update('deals', record.id, { archived: true });
            }}>
              <NexusIcon name="Archive" size={11} /><span>Archive</span>
            </button>
          )}
          {type === 'deals' && canManage && record.archived && (
            <button role="menuitem" className="nx-chip-menu-item" onClick={() => {
              setOpen(false);
              store.update('deals', record.id, { archived: false });
            }}>
              <NexusIcon name="ArchiveRestore" size={11} /><span>Restore</span>
            </button>
          )}
          {type === 'tasks' && record.status === 'open' && (
            <button type="button" role="menuitem" className="nx-chip-menu-item" onClick={() => {
              setOpen(false);
              store.startTask(record.id);
            }}>
              <NexusIcon name="Play" size={11} /><span>Start</span>
            </button>
          )}
          {type === 'tasks' && (record.status === 'cancelled' || record.done) && (
            <button type="button" role="menuitem" className="nx-chip-menu-item" onClick={() => {
              setOpen(false);
              store.update('tasks', record.id, { done: false });
            }}>
              <NexusIcon name="RotateCcw" size={11} /><span>Reopen</span>
            </button>
          )}
          {type === 'tasks' && !record.done && record.status !== 'cancelled' && (
            <button type="button" role="menuitem" className="nx-chip-menu-item" onClick={() => {
              setOpen(false);
              store.cancelTask(record.id);
            }}>
              <NexusIcon name="XCircle" size={11} /><span>Cancel</span>
            </button>
          )}
          {type === 'tasks' && canManage && !record.archived && (
            <button role="menuitem" className="nx-chip-menu-item" onClick={() => {
              setOpen(false);
              store.archiveTask(record.id);
            }}>
              <NexusIcon name="Archive" size={11} /><span>Archive</span>
            </button>
          )}
          {type === 'companies' && canManage && (
            <button role="menuitem" className="nx-chip-menu-item" onClick={() => {
              setOpen(false);
              store.confirm({
                title: 'Archive this company?',
                description: 'The company will be hidden from all views. Linked contacts and deals are not affected.',
                onConfirm: () => store.update('companies', record.id, { archived: true }),
              });
            }}>
              <NexusIcon name="Archive" size={11} /><span>Archive</span>
            </button>
          )}
          {canManage && (
            <button role="menuitem" className="nx-chip-menu-item is-danger" onClick={() => {
              setOpen(false);
              store.confirm({
                title: 'Delete this record?',
                description: 'Linked deals, tasks, and conversations will be cleaned up.',
                danger: true,
                onConfirm: () => store.remove(type, record.id),
              });
            }}>
              <NexusIcon name="Trash" size={11} /><span>Delete</span>
            </button>
          )}
        </div>
      )}
    </span>
  );
};

/* ---------- SortableTh ---------- */
export const SortableTh = ({ children, icon, sticky, field, sort, onSort, style }) => {
  const isActive = sort && sort.field === field;
  const ariaSort = isActive ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none';
  return (
    <th
      scope="col"
      className={sticky ? 'is-sticky' : ''}
      onClick={() => onSort && field && onSort(field)}
      style={{ cursor: field ? 'pointer' : 'default', ...(style?.width && !style?.minWidth ? { minWidth: style.width } : {}), ...style }}
      aria-sort={field ? ariaSort : undefined}
    >
      <span className="nx-th-inner">
        {icon && <NexusIcon name={icon} size={12} />}
        <span>{children}</span>
        {field && <NexusIcon name={isActive ? (sort.dir === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronDown'} size={11} className="nx-th-sort" style={{ opacity: isActive ? 1 : 0.3 }} />}
      </span>
    </th>
  );
};
